import React, { useEffect, useRef, useState, Suspense } from 'react';
import { Badge, Button, Heading, TextStyle } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { AccountClient } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import {
  AttachmentPreviewer,
  COMMON_CONSTANTS,
  getTranslatedText,
  NoDataCard,
  SkeletonTableLoading
} from '@nile/components';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  dompurifier,
  getDownloadURL
} from '@nile/components/src/Utils/helpers';
import {
  loadArticle,
  purifyTags
} from '@nile/rmm/src/modules/knowledge-base/helper';
import { useMutation } from '@apollo/client';
import { WHITELISTED_ATTR } from '@nile/rmm/src/modules/knowledge-base/helper/constants';
import { isEmpty } from 'lodash';
import { KB_CONSTANTS } from '@nile/rmm/src/modules/knowledge-base/constants/kb-constants';
import Loading from '@nile/components/src/Svg/AnimatedSvg/Loading.svg';
import { GET_KB_ITEM, UDPATE_ARTICLE_VIEW_COUNT } from '../../graphql';

// Lazy load NotionEditor to avoid bundling 16MB for all users
const NotionEditor = React.lazy(() =>
  import('@nile/components/src/NotionEditor')
);

const SuperPdfViewer = React.lazy(() =>
  import('@nile/components/src/AttachmentPreview/SuperPdfViewer')
);

const BADGE_LABEL = {
  REQUESTER: 'common.app.kb.article.suggestion.requester.articles',
  TECHNICIAN: 'common.app.kb.article.suggestion.technician.articles'
};

function ArticleReader({
  selectedArticle,
  setSelectedArticle,
  insertLink,
  closeSheet,
  hideActions = false,
  hideToolBar = false,
  sendViewCount = false,
  ApiClient,
  hide,
  token,
  hideDraft = false
}) {
  const [articleLoading, setArticleLoading] = useState(false);
  const [content, setContent] = useState('');
  const [article, setArticle] = useState(null);
  const [formatterAttachment, setFormatterAttachment] = useState([]);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });
  const notionRef = useRef(null);
  const intl = useIntl();

  const articleUserType =
    selectedArticle?.userType || article?.visibilityType === 'PUBLIC'
      ? 'REQUESTER'
      : 'TECHNICIAN';

  const client = ApiClient || AccountClient;

  const isDraftArticle = hideDraft && article?.status === 'DRAFT';

  const [updateViewCount] = useMutation(UDPATE_ARTICLE_VIEW_COUNT, {
    client: client,
    onCompleted: (res) => {
      if (res?.incrementArticleViews && article?.itemId) {
        const viewedArticles = JSON.parse(
          sessionStorage.getItem('viewedArticles') || '[]'
        );
        viewedArticles.push(article.itemId);
        sessionStorage.setItem(
          'viewedArticles',
          JSON.stringify(viewedArticles)
        );
      }
    }
  });

  useEffect(() => {
    if (article?.itemId && sendViewCount) {
      const viewedArticles = JSON.parse(
        sessionStorage.getItem('viewedArticles') || '[]'
      );
      if (!viewedArticles.includes(article.itemId)) {
        updateViewCount({
          variables: {
            input: {
              article: {
                itemId: article?.itemId
              }
            }
          }
        });
      }
    }
  }, [article?.itemId]);

  function formatAttachmentForPreview(attachment, cb) {
    try {
      const module = 'KB_ITEM_ATTACHMENT';
      const details = {
        itemId: article?.itemId,
        itemType: KB_CONSTANTS.KB_ARTICLE,
        fileName: attachment?.url
      };

      const isPdf = attachment.fileExt === 'pdf';

      const getUrl = (download = false) => {
        if (isPdf && !download) {
          return 'preview';
        }
        if (token) {
          return 'direct_download';
        }
        return 'download';
      };
      const urls = {
        url: getDownloadURL({
          module,
          details,
          endPoint: getUrl()
        }),
        ...(isPdf && {
          downloadUrl: getDownloadURL({
            module,
            details,
            endPoint: getUrl(true)
          })
        })
      };

      setFormatterAttachment([
        {
          name: attachment.fileName,
          ...urls,
          type: isPdf ? 'superpdf' : attachment.fileExt
        }
      ]);

      if (cb) cb();
    } catch (err) {
      console.log('Error while opening attachment');
    }
  }

  function openAttachmentPreview(attachment) {
    if (attachment) {
      formatAttachmentForPreview(attachment, () => {
        setShowAttachmentPreview({
          ...showAttachmentPreview,
          show: true,
          index: 0
        });
      });
    }
  }

  const closePreviwer = () => {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: false
    });
  };

  const downloadAttachment = (attachment) => {
    if (isEmpty(attachment)) return;
    openAttachmentPreview(attachment);
  };

  const getArticle = async (id) => {
    setArticleLoading(true);
    client
      .query({
        query: GET_KB_ITEM,
        fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
        variables: {
          input: {
            itemId: id
          }
        }
      })
      .then(async (res) => {
        const { contentUrl } = res?.data?.getKbItem || {};
        setArticle(res?.data?.getKbItem);
        const resArticle = res?.data?.getKbItem;
        if (contentUrl && resArticle?.articleType !== 'PDF') {
          let articleContent = await loadArticle(contentUrl, token);
          articleContent = dompurifier(articleContent, {
            ...WHITELISTED_ATTR
          });
          articleContent = purifyTags(articleContent);
          setContent(articleContent);
        }
        setArticleLoading(false);
      })
      .catch((err) => {
        setArticleLoading(false);
      });
  };

  useEffect(() => {
    if (selectedArticle) {
      getArticle(selectedArticle?.id);
    }
  }, [selectedArticle?.id]);

  useEffect(() => {
    notionRef.current?.refreshContent(content);
  }, [content]);

  return (
    <div className="flex fcol g20 full-height">
      {!hideToolBar && (
        <div className="flex">
          {!hideActions && !hide?.back && (
            <div className="flex">
              <Button
                size="slim"
                plain
                monochrome
                icon={<SVGIcon name="BackArrow" />}
                onClick={() => setSelectedArticle(null)}
              />
            </div>
          )}
          <div className="flex f11 g10 jend">
            {!hideActions && !articleLoading && !isDraftArticle && article && (
              <>
                <Badge status="success" capitalize>
                  <FormattedMessage
                    id={BADGE_LABEL?.[articleUserType] || 'app.noData.message'}
                  />
                </Badge>
                <Button
                  icon={<SVGIcon name="ExternalUrl" />}
                  size="slim"
                  url={`#/kb/article/${selectedArticle?.id}/read`}
                />
                {selectedArticle?.visibilityType === 'PUBLIC' && !hide?.insert && (
                  <Button
                    icon={<SVGIcon name="Link" />}
                    onClick={() => insertLink(selectedArticle)}
                    size="slim"
                  >
                    {`${getTranslatedText(
                      intl,
                      'common.insert'
                    )} ${getTranslatedText(intl, 'common.link')}`}
                  </Button>
                )}
              </>
            )}
            <Button
              icon={<SVGIcon name="Close" />}
              onClick={closeSheet}
              size="slim"
              plain
              monochrome
            />
          </div>
        </div>
      )}
      <div className="full-height">
        {articleLoading ? (
          <SkeletonTableLoading
            tableRow={5}
            tableColumn={1}
            tableHeader={false}
          />
        ) : (
          <>
            {isDraftArticle ? (
              <div className="flex-center full-height">
                <div className="flex actr fcol g20 p10">
                  <NoDataCard
                    imageURL={`url(${envConfig?.imageRoot}svg/articleEmptyResult.svg)`}
                    description={
                      <TextStyle weight="Medium">
                        <FormattedMessage id="common.app.kb.article.runbook.draft.restrict" />
                      </TextStyle>
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="flex full-height fcol">
                <div className="flex fcol g20 p10 break-word">
                  {article?.name && (
                    <Heading element="h1">{article?.name}</Heading>
                  )}
                  {article?.description && (
                    <TextStyle variation="subdued" weight="Medium">
                      {article?.description}
                    </TextStyle>
                  )}
                </div>
                {article?.articleType === 'PDF' ? (
                  <div className="article-content h85p scroll">
                    <SuperPdfViewer
                      url={article?.contentUrl}
                      hideToolbar
                      token={token}
                    />
                  </div>
                ) : (
                  <div className="article-content h85p scroll">
                    <Suspense
                      fallback={
                        <div className="flex actr jctr fh">
                          <div className="header-img-size">
                            <Loading />
                          </div>
                        </div>
                      }
                    >
                      <NotionEditor
                        content={content}
                        ref={notionRef}
                        readOnlyMode
                        downloadAttachment={downloadAttachment}
                        fullScreen
                      />
                    </Suspense>
                    {showAttachmentPreview.show && (
                      <AttachmentPreviewer
                        showPreviewer={showAttachmentPreview.show}
                        closePreviwer={closePreviwer}
                        list={formatterAttachment}
                        selectedIndex={showAttachmentPreview.index}
                        token={token}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ArticleReader;
